import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { bonusesApi } from '../api/bonuses.api';
import type { CreateBonusRuleDto, UpdateBonusRuleDto, ManualBonusAdjustDto } from '../types';

export const useBonusRules = () => {
  return useQuery({
    queryKey: ['bonus-rules'],
    queryFn: () => bonusesApi.getAllRules(),
  });
};

export const useBonusRule = (id: number) => {
  return useQuery({
    queryKey: ['bonus-rules', id],
    queryFn: () => bonusesApi.getRule(id),
    enabled: !!id,
  });
};

export const useCreateBonusRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBonusRuleDto) => bonusesApi.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus-rules'] });
      notifications.show({
        title: 'Успешно',
        message: 'Правило успешно создано',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Не удалось создать правило',
        color: 'red',
      });
    },
  });
};

export const useUpdateBonusRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBonusRuleDto }) =>
      bonusesApi.updateRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus-rules'] });
      notifications.show({
        title: 'Успешно',
        message: 'Правило успешно обновлено',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Не удалось обновить правило',
        color: 'red',
      });
    },
  });
};

export const useDeleteBonusRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => bonusesApi.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus-rules'] });
      notifications.show({
        title: 'Успешно',
        message: 'Правило успешно удалено',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Не удалось удалить правило',
        color: 'red',
      });
    },
  });
};

export const useRunActivatePendingCron = () => {
  return useMutation({
    mutationFn: () => bonusesApi.runActivatePendingCron(),
    onSuccess: (data) => {
      const parts = [
        `дат обновлено: ${data.datesAdjusted}`,
        `активировано: ${data.activated}`,
      ];
      notifications.show({
        title: 'Успешно',
        message: `Активация бонусов (${parts.join(', ')})`,
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Не удалось запустить крон',
        color: 'red',
      });
    },
  });
};

export const useRunExpireCron = () => {
  return useMutation({
    mutationFn: () => bonusesApi.runExpireCron(),
    onSuccess: (data) => {
      const msg =
        data.expired > 0
          ? `Сгорело транзакций: ${data.expired}, сумма: ${data.expiredAmount}`
          : 'Крон сгорания выполнен, истекших бонусов нет';
      notifications.show({
        title: 'Успешно',
        message: msg,
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Не удалось запустить крон сгорания',
        color: 'red',
      });
    },
  });
};

export const useRunSendExpiringRemindersCron = () => {
  return useMutation({
    mutationFn: () => bonusesApi.runSendExpiringRemindersCron(),
    onSuccess: () => {
      notifications.show({
        title: 'Успешно',
        message: 'Крон пушей «скоро сгорят бонусы» выполнен',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Не удалось запустить крон напоминаний',
        color: 'red',
      });
    },
  });
};

export const useBonusUsers = (
  phone?: string,
  options?: { onlyShopClients?: boolean },
) => {
  const onlyShopClients = options?.onlyShopClients === true;
  return useQuery({
    queryKey: ['bonus-users', phone ?? '', onlyShopClients ? 'shop' : 'all'],
    queryFn: () => bonusesApi.getUsers(phone, { onlyShopClients }),
  });
};

export const useManualBonusAdjust = () => {
  return useMutation({
    mutationFn: (data: ManualBonusAdjustDto) => bonusesApi.manualAdjust(data),
    onSuccess: (_, variables) => {
      const action = variables.type === 'accrue' ? 'Начисление' : 'Списание';
      notifications.show({
        title: 'Успешно',
        message: `${action} бонусов выполнено`,
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Не удалось выполнить операцию',
        color: 'red',
      });
    },
  });
};
